import { type ReactElement, type RefAttributes, useCallback } from 'react';
import { IoIosArrowDown, IoIosArrowForward } from 'react-icons/io';

import { Box, type SxProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { SimpleTreeView, type SimpleTreeViewProps } from '@mui/x-tree-view';

import { useTreeNodes } from '@components/common/TreeView/hooks/useTreeNodes';

export interface ITreeViewSxProperties {
  item?: SxProps<Theme>;
  tree?: SxProps<Theme>;
  container?: SxProps<Theme>;
}

export interface ICustomFrontIcon {
  type: string;
  tooltip?: string;
  icon?: ReactElement;
}

export interface IRenderTreeData {
  name: string;
  enabled: boolean;
  id: number | string;
  children: IRenderTreeData[];
}

export type TreeViewProperties = {
  disabled?: boolean;
  withCopy?: boolean;
  data: IRenderTreeData[];
  sxProps?: ITreeViewSxProperties;
  customFrontIcon?: null | ICustomFrontIcon;
} & RefAttributes<HTMLDivElement> &
  SimpleTreeViewProps<boolean | undefined>;

export const TreeViewComponent = ({
  data,
  slots,
  disabled = false,
  withCopy = false,
  multiSelect = false,
  defaultSelectedItems,
  customFrontIcon = null,
  defaultExpandedItems = [],
  sxProps: { tree, item, container } = {},
}: TreeViewProperties) => {
  const collapseIcon = slots?.collapseIcon ?? IoIosArrowDown;
  const expandIcon = slots?.expandIcon ?? IoIosArrowForward;

  const renderCustomFrontIcon = useCallback(
    (node: IRenderTreeData) => {
      if (customFrontIcon?.type === 'indicateInactiveState') {
        return node.enabled ? null : customFrontIcon;
      }

      return customFrontIcon;
    },
    [customFrontIcon]
  );

  const treeNodes = useTreeNodes({
    data,
    disabled,
    withCopy,
    itemSx: item,
    customFrontIcon,
    renderCustomFrontIcon,
  });

  return (
    <Box sx={container}>
      <SimpleTreeView
        slots={{
          ...slots,
          expandIcon,
          collapseIcon,
        }}
        aria-label="customized tree view"
        defaultExpandedItems={defaultExpandedItems}
        defaultSelectedItems={defaultSelectedItems}
        multiSelect={multiSelect}
        sx={{ overflowX: 'hidden', ...tree }}
      >
        {treeNodes}
      </SimpleTreeView>
    </Box>
  );
};
